import { useChat } from '@ai-sdk/react';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { convertToAIMessage } from './messageHelper';
const MAX_PROCESSED_MESSAGES = 1000; // Maximum number of message IDs to keep in memory
const MAX_STEPS = 10;
export function createChatStrategy(config) {
    return {
        useChat(context, props) {
            // Track message IDs that have been processed to avoid duplicates
            // Using Map to maintain insertion order for LRU-style cleanup
            const processedMessageIds = useRef(new Map());
            // Track auto-reply state
            const autoReplyTriggeredRef = useRef(false);
            // Track pending reply promises with sequence correlation
            const pendingReplyPromises = useRef(new Map());
            // Track reply sequence numbers
            const replySequenceRef = useRef(0);
            const expectedResponseSequenceRef = useRef(0);
            // Helper function to add message ID with buffer size management
            const addProcessedMessageId = useCallback((messageId) => {
                const map = processedMessageIds.current;
                // Add the new message ID
                map.set(messageId, true);
                // If we exceed the buffer size, remove the oldest entries
                if (map.size > MAX_PROCESSED_MESSAGES) {
                    const entriesToDelete = map.size - MAX_PROCESSED_MESSAGES;
                    const keysToDelete = Array.from(map.keys()).slice(0, entriesToDelete);
                    keysToDelete.forEach((key) => map.delete(key));
                }
            }, []);
            const lastAppendedContext = useRef(undefined);
            // Helper function to check if message ID has been processed
            const hasProcessedMessageId = useCallback((messageId) => {
                return processedMessageIds.current.has(messageId);
            }, []);
            // Convert messages from props to AI SDK format for initial messages only
            const initialMessages = useMemo(() => {
                const messages = (props.messages || []).map(convertToAIMessage);
                // Mark initial messages as processed
                // Handle messages that have context but no parts (context-only messages)
                const messagesWithParts = messages.filter((msg) => msg.content);
                const contextOnlyMessages = (props.messages || [])
                    .filter((message) => {
                    const hasNoParts = !message.parts ||
                        message.parts.length === 0 ||
                        message.parts.every((part) => part.type === 'text' && !part.text?.trim());
                    const hasContext = message.context && message.context.length > 0;
                    return hasNoParts && hasContext;
                })
                    .map((message) => {
                    const contextDescription = message.context
                        ?.map((item) => {
                        if (item.kind === 'solution') {
                            return `Referenced solution: ${item.data?.solutionId || 'unknown'}`;
                        }
                        return `Referenced ${item.kind}`;
                    })
                        .join(', ') || 'Referenced context';
                    return {
                        id: message.id,
                        role: message.role ?? 'user',
                        content: contextDescription,
                        context: message.context,
                        createdAt: message.createdAt,
                    };
                });
                const allMessages = [...messagesWithParts, ...contextOnlyMessages];
                allMessages.forEach((message) => {
                    addProcessedMessageId(message.id);
                });
                return allMessages;
            }, [props.messages, addProcessedMessageId]);
            const chat = useChat({
                api: '/api/v1/assistant/chat',
                headers: {
                    // TODO extract constant. this needs to match the configuration in the assistant preset
                    'x-workspace-id': props.options.workspace,
                },
                initialMessages,
                // In order to automatically send another request to the server when all tool calls are server-side, you
                // need to set maxSteps to a value greater than 1 in the useChat options. It is
                // disabled by default for backward compatibility.
                // See https://github.com/vercel/ai/discussions/2148#discussioncomment-9919692
                maxSteps: MAX_STEPS,
                fetch(input, init) {
                    return fetch(input, {
                        ...init,
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${props.token}`,
                        },
                    });
                },
                onError(error) {
                    console.error('Chat error:', error);
                    // Reject any pending reply promises
                    const promises = Array.from(pendingReplyPromises.current.values());
                    pendingReplyPromises.current.clear();
                    promises.forEach(({ reject }) => reject(error));
                    if (error.message.includes('tool') ||
                        error.message.includes('function')) {
                        // Likely a tool-related error
                        console.log('tool-related error');
                        chat.append({
                            role: 'assistant',
                            content: `I'm sorry, the tool failed to execute with error (truncated): ${error.message.substring(0, 500)}. Could you please try again or rephrase your request?`,
                        });
                    }
                    else {
                        throw error;
                    }
                    throw error;
                },
                onResponse(response) {
                    console.log('Chat response received:', response.status, response.statusText);
                },
                async onToolCall({ toolCall, ...rest }) {
                    const parsedToolcall = toolCall;
                    console.log('toolCall', parsedToolcall);
                    // if (parsedToolcall.toolName === 'start_solution') {
                    //     for (const artifact of parsedToolcall.result?.artifacts || []) {
                    //         if (artifact.kind === 'document') {
                    //             const response = await props.onArtifact?.({
                    //                 kind: 'document',
                    //                 action: 'create',
                    //                 id: artifact.id,
                    //             });
                    //             console.log('artifact created', response);
                    //             continue;
                    //         }
                    //     }
                    // }
                    // if (parsedToolcall.toolName === 'apply_suggestion') {
                    //     return props.onSuggestion(parsedToolcall.args.suggestion);
                    // }
                    // if (parsedToolcall.toolName === 'update_thread') {
                    //     const result = await props.onContextUpdate(parsedToolcall.args);
                    //     const response: UpdateThreadToolInvocationOutput = {
                    //         updated: true,
                    //     };
                    //     return response;
                    // }
                    if (parsedToolcall.toolName === 'replace_document') {
                        const blocks = parsedToolcall.args.updated_blocks.map((block) => {
                            if (block.kind === 'text') {
                                return {
                                    kind: 'text',
                                    items: [
                                        {
                                            kind: 'text',
                                            text: block.text,
                                        },
                                    ],
                                };
                            }
                            if (block.kind === 'list_item') {
                                return {
                                    kind: 'list_item',
                                    style: block.style,
                                    items: [
                                        {
                                            kind: 'text',
                                            text: block.text,
                                        },
                                    ],
                                };
                            }
                            if (block.kind === 'callout') {
                                return {
                                    kind: 'callout',
                                    text: block.text,
                                };
                            }
                            const never = block;
                            throw new Error('unsupported block kind');
                        });
                        // console.log('replacing document', {
                        //     document: parsedToolcall.args.document,
                        //     blocks,
                        // });
                        await props.onReplaceDocument({
                            id: parsedToolcall.args.document,
                            blocks: blocks,
                        });
                        const response = {
                            document: parsedToolcall.args.document,
                            updated_blocks: parsedToolcall.args.updated_blocks,
                        };
                        return response;
                    }
                    if (parsedToolcall.toolName === 'create_document') {
                        const response = await props.onCreateDocument({
                            title: parsedToolcall.args.title,
                            parent: parsedToolcall.args.parent ?? undefined,
                            icon: parsedToolcall.args.icon ?? undefined,
                            type: 'document',
                            blocks: parsedToolcall.args.blocks.flatMap((item) => {
                                if (item.kind === 'text') {
                                    return [
                                        {
                                            kind: 'text',
                                            items: [
                                                {
                                                    kind: 'text',
                                                    text: item.text,
                                                },
                                            ],
                                        },
                                    ];
                                }
                                if (item.kind === 'callout') {
                                    return [
                                        {
                                            kind: 'callout',
                                            text: item.text,
                                        },
                                    ];
                                }
                                if (item.kind === 'list_item') {
                                    return [
                                        {
                                            kind: 'list_item',
                                            style: item.style,
                                            items: [
                                                {
                                                    kind: 'text',
                                                    text: item.text,
                                                },
                                            ],
                                        },
                                    ];
                                }
                                const never = item;
                                throw new Error(`unsupported block kind: ${JSON.stringify(never)}`);
                            }),
                        });
                        return {
                            document: {
                                id: response.id,
                                title: parsedToolcall.args.title,
                                blocks: parsedToolcall.args.blocks,
                            },
                        };
                    }
                },
                experimental_prepareRequestBody(options) {
                    const additional = {
                        mode: props.options.mode,
                        tag: props.options.tag,
                        threadId: props.threadId,
                        environmentMode: props.options.environmentMode,
                        // workspace: props.options.workspace,
                    };
                    let lastMessage = options.messages[options.messages.length - 1];
                    // @ts-expect-error
                    if (!lastMessage.context || lastMessage.context.length > 0) {
                        lastMessage = {
                            ...lastMessage,
                            // @ts-expect-error
                            context: lastAppendedContext.current,
                        };
                    }
                    return {
                        ...additional,
                        // message: lastMessage,
                        messages: options.messages.map((message) => ({
                            ...message,
                        })),
                    };
                },
                onFinish(message, options) {
                    console.log('chat.onFinish called:', {
                        messageId: message.id,
                        role: message.role,
                        content: message.content?.slice(0, 100) + '...',
                        hasContent: !!message.content,
                        hasParts: !!message.parts?.length,
                        options,
                    });
                    // Save completed assistant messages using the appropriate callback
                    if (message.role === 'assistant') {
                        // Resolve the next expected reply promise in sequence
                        const expectedSequence = expectedResponseSequenceRef.current;
                        try {
                            // Convert AI SDK parts to our schema format, fallback to content
                            let parts = [];
                            if (message.parts && message.parts.length > 0) {
                                parts = message.parts.flatMap((part) => {
                                    if (part.type === 'text') {
                                        return [
                                            {
                                                type: 'text',
                                                text: part.text || '',
                                            },
                                        ];
                                    }
                                    if (part.type === 'step-start') {
                                        return [
                                            {
                                                type: 'step-start',
                                            },
                                        ];
                                    }
                                    if (part.type === 'tool-invocation') {
                                        part.toolInvocation.toolCallId;
                                        // Properly convert tool invocation to our schema
                                        return [
                                            {
                                                type: 'tool-invocation',
                                                toolInvocation: {
                                                    toolName: (part.toolInvocation
                                                        ?.toolName || 'unknown'),
                                                    args: part.toolInvocation?.args || {},
                                                    callId: part.toolInvocation?.toolCallId ??
                                                        null,
                                                    result: 'result' in part.toolInvocation &&
                                                        part.toolInvocation.result
                                                        ? part.toolInvocation.result
                                                        : undefined,
                                                    state: (part.toolInvocation?.state ===
                                                        'result'
                                                        ? 'result'
                                                        : 'call'),
                                                    step: part.toolInvocation?.step || 0,
                                                },
                                            },
                                        ];
                                    }
                                    // Skip unsupported part types
                                    console.warn('Unsupported part type:', part.type);
                                    return [];
                                });
                            }
                            // Fallback to content if no valid parts
                            if (parts.length === 0) {
                                parts = [
                                    {
                                        type: 'text',
                                        text: message.content || '',
                                    },
                                ];
                            }
                            // Create the chat message
                            const chatMessage = {
                                id: message.id,
                                role: 'assistant',
                                parts,
                                context: [],
                                createdAt: message.createdAt || new Date(),
                            };
                            const pendingPromise = pendingReplyPromises.current.get(expectedSequence);
                            if (pendingPromise) {
                                pendingReplyPromises.current.delete(expectedSequence);
                                expectedResponseSequenceRef.current++;
                                pendingPromise.resolve(chatMessage);
                            }
                            // Check if this message has been processed before
                            const isNewMessage = !hasProcessedMessageId(message.id);
                            if (isNewMessage) {
                                // Mark as processed and call onAppendMessage for new messages
                                addProcessedMessageId(message.id);
                                props.onMessageCreate(chatMessage);
                            }
                            else {
                                // Call onMessageUpdate for existing messages
                                props.onMessageUpdate(chatMessage);
                            }
                        }
                        catch (error) {
                            console.warn('Failed to store assistant message:', error);
                        }
                    }
                    props.onGenerated();
                },
            });
            // Auto-reply logic: trigger assistant response when conditions are met
            useEffect(() => {
                async function checkAndTriggerAutoReply() {
                    // Check if auto-reply is enabled at the provider level
                    if (!config.autoReply?.enabled) {
                        return;
                    }
                    const messages = props.messages || [];
                    // Check if there are any messages
                    if (messages.length === 0) {
                        // Reset trigger flag when no messages
                        autoReplyTriggeredRef.current = false;
                        return;
                    }
                    // Default condition: last message is from user AND no assistant messages exist
                    const defaultCondition = (messages) => {
                        const lastMessage = messages[messages.length - 1];
                        const hasAssistantMessages = messages.some((msg) => msg.role === 'assistant');
                        return lastMessage?.role === 'user' && !hasAssistantMessages;
                    };
                    // Use custom condition if provided, otherwise use default
                    const shouldTriggerAutoReply = config.autoReply.condition
                        ? config.autoReply.condition(messages)
                        : defaultCondition(messages);
                    // Reset trigger flag if conditions are not met
                    if (!shouldTriggerAutoReply) {
                        autoReplyTriggeredRef.current = false;
                        return;
                    }
                    // Check if already triggered for this state
                    if (autoReplyTriggeredRef.current) {
                        return;
                    }
                    // All conditions met - trigger auto-reply
                    autoReplyTriggeredRef.current = true;
                    try {
                        await chat.reload();
                    }
                    catch (error) {
                        console.error('Failed to trigger auto-reply:', error);
                        autoReplyTriggeredRef.current = false; // Allow retry on next effect
                    }
                }
                checkAndTriggerAutoReply();
            }, [props.messages, config.autoReply, chat]);
            const appendMessage = useCallback(async (message) => {
                lastAppendedContext.current = message.context;
                // Create the user message and persist it via onAppendMessage
                const messageId = `msg-${Date.now()}`;
                const chatMessage = {
                    id: messageId,
                    role: 'user',
                    parts: message.parts,
                    context: message.context || [],
                    createdAt: new Date(),
                    idempotencyKey: message.idempotencyKey,
                };
                console.log('appending message', chatMessage);
                // Mark user message as processed and persist via callback
                addProcessedMessageId(messageId);
                props.onMessageCreate(chatMessage);
                // Create a message for the AI SDK
                const aiMessage = {
                    id: messageId,
                    role: 'user',
                    content: message.parts
                        .map((part) => {
                        if (part.type === 'tool-invocation') {
                            return JSON.stringify(part.toolInvocation);
                        }
                        if (part.type === 'text') {
                            return part.text;
                        }
                        return [];
                    })
                        .join('. '),
                    // @ts-expect-error
                    context: message.context,
                    idempotencyKey: message.idempotencyKey,
                    createdAt: new Date(),
                };
                await chat.append(aiMessage, {
                    body: {
                        context: message.context,
                        idempotencyKey: message.idempotencyKey,
                    },
                });
            }, [chat, props, addProcessedMessageId]);
            // Convert AI SDK messages to our format for display
            const messages = useMemo(() => {
                return chat.messages.map((aiMessage) => ({
                    id: aiMessage.id,
                    role: aiMessage.role ?? 'user',
                    parts: aiMessage.parts && aiMessage.parts.length > 0
                        ? aiMessage.parts.flatMap((part) => {
                            if (part.type === 'text') {
                                return [
                                    { type: 'text', text: part.text || '' },
                                ];
                            }
                            if (part.type === 'step-start') {
                                return [{ type: 'step-start' }];
                            }
                            if (part.type === 'tool-invocation') {
                                // Properly convert tool invocation to our schema
                                return [
                                    {
                                        type: 'tool-invocation',
                                        toolInvocation: {
                                            toolName: (part.toolInvocation
                                                ?.toolName || 'unknown'),
                                            callId: part.toolInvocation
                                                ?.toolCallId ?? null,
                                            args: part.toolInvocation?.args || {},
                                            result: 'result' in
                                                part.toolInvocation &&
                                                part.toolInvocation.result
                                                ? part.toolInvocation.result
                                                : undefined,
                                            state: (part.toolInvocation
                                                ?.state === 'result'
                                                ? 'result'
                                                : 'call'),
                                            step: part.toolInvocation?.step || 0,
                                        },
                                    },
                                ];
                            }
                            console.warn('Unsupported part type:', part.type);
                            return [];
                        })
                        : [{ type: 'text', text: aiMessage.content || '' }],
                    // @ts-expect-error
                    context: aiMessage.context || [],
                    createdAt: aiMessage.createdAt ?? new Date(),
                }));
            }, [chat.messages]);
            // Get the current streaming message (last message if it's being streamed)
            const streamingMessage = useMemo(() => {
                if (chat.status !== 'streaming' || chat.messages.length === 0) {
                    return null;
                }
                const lastMessage = chat.messages[chat.messages.length - 1];
                if (!lastMessage || lastMessage.role !== 'assistant') {
                    return null;
                }
                return messages[messages.length - 1] || null;
            }, [chat.status, chat.messages, messages]);
            return {
                async reload() {
                    await chat.reload();
                },
                async reply(message) {
                    return new Promise((resolve, reject) => {
                        // Generate sequence number for this reply
                        const sequence = replySequenceRef.current++;
                        const messageId = `reply-${sequence}-${Date.now()}`;
                        // Add this promise to the pending map with sequence
                        pendingReplyPromises.current.set(sequence, {
                            resolve,
                            reject,
                            messageId,
                        });
                        // Send the message with sequence marker
                        const content = message.parts
                            .map((part) => {
                            if (part.type === 'text') {
                                return part.text;
                            }
                            return '';
                        })
                            .join(' ');
                        chat.append({
                            role: 'system',
                            content: content,
                            // @ts-expect-error
                            context: message.context,
                            createdAt: new Date(),
                        }).catch(reject); // Reject if append fails
                    });
                },
                async commit(tool) {
                    console.log('committing tool', tool);
                    chat.addToolResult({
                        toolCallId: tool.callId,
                        result: tool.result,
                    });
                },
                append: appendMessage,
                status: chat.status,
                messages,
                isLoading: chat.status === 'streaming' ||
                    chat.status === 'submitted' ||
                    props.threadState === 'preparing',
                streamingMessage,
            };
        },
    };
}
//# sourceMappingURL=chatProvider.js.map